/**
 * Clock-style formatting for a live countdown to a deadline.
 *
 * The output is a digit + separator token (`4:07`, `1:23:45`), which is why
 * every caller renders it inside `<LtrToken>`: under an RTL document the bidi
 * algorithm would otherwise reorder the colon-separated run.
 *
 * The token carries no words, so it needs no translation — but it does need to
 * be visibly LIVE. A duration rendered once ("40m") is indistinguishable from
 * the session's fixed length, which is exactly how the emulation banner read
 * for its whole life.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/**
 * Format the milliseconds left until a deadline as `M:SS`, or `H:MM:SS` once an
 * hour or more remains (emulation sessions run up to 8 hours).
 *
 * Rounds UP, so a deadline set 40 minutes out reads `40:00` on the first paint
 * rather than `39:59` — the few hundred milliseconds the round trip cost should
 * not make a fresh session look already spent. A past deadline is `0:00`, never
 * a negative token.
 */
export function formatCountdown(msRemaining: number): string {
  const totalSeconds = Math.max(0, Math.ceil(msRemaining / MS_PER_SECOND));
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  const totalMinutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);

  const paddedSeconds = String(seconds).padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
  }
  return `${minutes}:${paddedSeconds}`;
}
